import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import config from '@/lib/config'
import type {
  NumberSchema,
  CreateNumberSchemaData,
  UpdateNumberSchemaData,
  SchemaType,
} from '../types/number-schema.types'

/**
 * Helper function for API calls with credentials
 */
async function fetchWithAuth(url: string, options: RequestInit = {}) {
  const token = localStorage.getItem('auth-data')
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(`${config.apiUrl}${url}`, {
    ...options,
    credentials: 'include',
    headers,
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(error.error || `HTTP ${response.status}`)
  }

  return response.json()
}

const NUMBER_SCHEMA_KEYS = {
  all: ['number-schemas'] as const,
  lists: () => [...NUMBER_SCHEMA_KEYS.all, 'list'] as const,
  list: (type?: SchemaType) =>
    [...NUMBER_SCHEMA_KEYS.lists(), { type }] as const,
  details: () => [...NUMBER_SCHEMA_KEYS.all, 'detail'] as const,
  detail: (id: string) => [...NUMBER_SCHEMA_KEYS.details(), id] as const,
  preview: (type: SchemaType, customerId?: string, template?: string) =>
    [...NUMBER_SCHEMA_KEYS.all, 'preview', type, customerId, template] as const,
}

/**
 * Get all number schemas
 */
export function useNumberSchemas(type?: SchemaType) {
  return useQuery<NumberSchema[]>({
    queryKey: NUMBER_SCHEMA_KEYS.list(type),
    queryFn: () => {
      const params = type ? `?type=${type}` : ''
      return fetchWithAuth(`/api/number-schemas${params}`)
    },
  })
}

/**
 * Get schema by ID
 */
export function useNumberSchema(id: string) {
  return useQuery<NumberSchema>({
    queryKey: NUMBER_SCHEMA_KEYS.detail(id),
    queryFn: () => fetchWithAuth(`/api/number-schemas/${id}`),
    enabled: !!id,
  })
}

/**
 * Create number schema
 */
export function useCreateNumberSchema() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateNumberSchemaData) =>
      fetchWithAuth('/api/number-schemas', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NUMBER_SCHEMA_KEYS.all })
    },
  })
}

/**
 * Update number schema
 */
export function useUpdateNumberSchema() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateNumberSchemaData }) =>
      fetchWithAuth(`/api/number-schemas/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: NUMBER_SCHEMA_KEYS.all })
      queryClient.invalidateQueries({
        queryKey: NUMBER_SCHEMA_KEYS.detail(variables.id),
      })
    },
  })
}

/**
 * Delete number schema
 */
export function useDeleteNumberSchema() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) =>
      fetchWithAuth(`/api/number-schemas/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NUMBER_SCHEMA_KEYS.all })
    },
  })
}

/**
 * Preview next number
 */
export function usePreviewNumber(
  type: SchemaType,
  customerId?: string,
  customerNumber?: string,
  customerName?: string,
  template?: string,
  date?: Date
) {
  const params = new URLSearchParams({ type })
  if (customerId) params.append('customerId', customerId)
  if (customerNumber) params.append('customerNumber', customerNumber)
  if (customerName) params.append('customerName', customerName)
  if (date) params.append('date', date.toISOString())
  if (template) params.append('template', template)

  return useQuery<{ preview: string }>({
    queryKey: NUMBER_SCHEMA_KEYS.preview(type, customerId, template),
    queryFn: () =>
      fetchWithAuth(`/api/number-schemas/preview/next-number?${params}`),
    enabled: !!type,
  })
}
